import React, {useState, useCallback, useMemo} from 'react';
import { Box, Grid, Divider, Link, Button, Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText, Menu, MenuItem, IconButton, Tooltip, TextField, Select, FormControl } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ErrorIcon from '@mui/icons-material/Error';

import { useNavigate, useOutletContext } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useUpdateSample, useMoveSampleToDataset } from '../services/api/KeynomeAPIMutations';
import { useSamples, useArchiveDestination } from '../services/api/KeynomeAPIHooks';

import IdentifiedSpeciesVersionDialog from '../Runs/IdentifiedSpeciesVersionDialog'

import { removePrefixFromAnalysisVersion } from '../Analyses/canonicalizeMetadata';

function DetailedSampleMetadataSummary(props) {
  const navigate = useNavigate();
  const {
    sample,
    analysis,
    activeProject,
    positiveIdentifiedSpeciesCt,
    runId
  } = props;
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const open = Boolean(menuAnchorEl);
  const [versionDialogOpen, setVersionDialogOpen] = useState(false);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editedSampleName, setEditedSampleName] = useState('');
  const [editedSampleType, setEditedSampleType] = useState('');
  const { setUserErrorMessage } = useOutletContext();
  const queryClient = useQueryClient();
  const updateSampleMutation = useUpdateSample();
  const moveSampleMutation = useMoveSampleToDataset();
  const { data: siblingSamples } = useSamples(runId);

  const organizationId = activeProject?.attributes?.organization_id;
  const { archiveDataset } = useArchiveDestination(organizationId, { includeDataset: true });

  const isDuplicateName = useMemo(() => {
    if (!editedSampleName.trim() || !siblingSamples) return false;
    return siblingSamples.some(
      s => s.id !== sample?.id && 
           s.attributes.sample_name?.toLowerCase() === editedSampleName.trim().toLowerCase()
    );
  }, [editedSampleName, siblingSamples, sample?.id]);

  const handleArchiveClick = () => {
    setArchiveDialogOpen(true);
  };

const handleMenuClick = (event) => {
  setMenuAnchorEl(event.currentTarget);
};

const handleMenuClose = () => {
  setMenuAnchorEl(null);
};


const handleArchiveConfirm = useCallback(async () => {
  try {
    setIsArchiving(true);
    await moveSampleMutation.mutateAsync({
      sampleId: sample.id,
      datasetId: archiveDataset.id
    });
    setArchiveDialogOpen(false);
    navigate('/');
  } catch (error) {
    setUserErrorMessage('Failed to archive sample! Please wait a few minutes and try again. Contact pathosight-support@cepheid.com if you require assistance.');
  } finally {
    setIsArchiving(false);
  }
}, [archiveDataset, sample, moveSampleMutation, navigate, setUserErrorMessage]);

  const handleArchiveCancel = () => {
    setArchiveDialogOpen(false);
  };

  const handleEditClick = () => {
    setEditedSampleName(sample?.attributes?.sample_name || '');
    setEditedSampleType(sample?.attributes?.sample_type || 'isolate');
    setIsEditing(true);
  };

  const handleCancelClick = () => {
    setIsEditing(false);
    setEditedSampleName('');
    setEditedSampleType('');
  };

  const handleSaveClick = async () => {
    setIsSaving(true);
    try {
      await updateSampleMutation.mutateAsync({
        sampleId: sample.id,
        sampleName: editedSampleName.trim(),
        sampleType: editedSampleType
      });

      queryClient.invalidateQueries({ queryKey: [`v1/lib_seqs/${sample.attributes.uuid}`] });
      queryClient.invalidateQueries({ queryKey: [`v1/sequencing_runs/${runId}/lib_seqs`] });
      setIsEditing(false);
    } catch (error) {
      if (error.statusCode >= 400 && error.statusCode < 500) {
        setUserErrorMessage('Failed to update sample! Please confirm that the sample name meets validation requirements and try again.');
      } else {
        setUserErrorMessage('Failed to update sample! Please wait a few minutes and try again. Contact pathosight-support@cepheid.com if you require assistance.');
      }
    } finally {
      setIsSaving(false);
    }
  };

  let analysisExecutionTimeString = null;
    if (analysis) {
        const diffMs = new Date(analysis.attributes.updated_at) - new Date(analysis.attributes.created_at)
        const diffMins = Math.round(diffMs / 60000);
        analysisExecutionTimeString = `(${diffMins} min.)`
    }
  return (
    <>
      <Box sx={{ backgroundColor: '#eee', padding: '1px 10px' }}>
        <Grid container>
          <Grid item xs={6}>
            <p><b>SAMPLE / ANALYSIS</b></p>
          </Grid>
          {archiveDataset ? (
            <Grid item xs={6} sx={{textAlign: 'right', paddingTop: "5px"}}>
              {isEditing && (
                <>
                  <Button
                    size="small"
                    color="success"
                    variant="contained"
                    onClick={handleSaveClick}
                    disabled={
                      isSaving || 
                      !editedSampleName.trim() || 
                      isDuplicateName ||
                      (editedSampleName === sample?.attributes?.sample_name && editedSampleType === sample?.attributes?.sample_type)
                    }
                    sx={{ mr: 1 }}
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    variant="outlined"
                    onClick={handleCancelClick}
                    disabled={isSaving}
                    sx={{ mr: 1 }}
                  >
                    Cancel
                  </Button>
                </>
              )}
              <IconButton
                onClick={handleMenuClick}
                disabled={isEditing}
              >
                <MoreVertIcon />
              </IconButton>
              <Menu
                anchorEl={menuAnchorEl}
                open={open}
                onClose={handleMenuClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                <MenuItem 
                  onClick={() => {
                    handleEditClick();
                    handleMenuClose();
                  }}
                  disabled={!sample?.attributes}
                >
                  Edit Sample Metadata
                </MenuItem>
                <MenuItem onClick={() => {
                  handleArchiveClick();
                  handleMenuClose();
                }}>
                  Archive Sample
                </MenuItem>
              </Menu>
            </Grid>
          ): null}
          </Grid>
      </Box>
      <Dialog
        open={archiveDialogOpen}
        onClose={handleArchiveCancel}
      >
        <DialogTitle>Archive Sample</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to archive this sample? After archiving, you will no longer be able to see any results associated with this sample.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleArchiveCancel} disabled={isArchiving}>Cancel</Button>
          <Button 
            onClick={handleArchiveConfirm}
            color="primary"
            variant="contained"
            autoFocus
            disabled={isArchiving}
          >
            {isArchiving ? 'Archiving...' : 'Yes, Archive'}
          </Button>
        </DialogActions>
      </Dialog>
      <Box sx={{ padding: '1px 10px' }}>
        {isEditing ? (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, mt: 1 }}>
            <b>Sample Name:</b>
            <TextField
              size="small"
              value={editedSampleName}
              onChange={(e) => setEditedSampleName(e.target.value)}
              disabled={isSaving}
              error={isDuplicateName}
              helperText={isDuplicateName ? "A sample with this name already exists in the dataset" : ""}
              sx={{ ml: 1, flex: 1 }}
            />
          </Box>
        ) : (
          <p>
            <b>Sample Name:</b>
            {' '}
            {sample?.attributes.sample_name || sample?.attributes.sample_id || sample?.attributes.library_id || 'N/A'}
          </p>
        )}
        {isEditing ? (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <b>Sample Type:</b>
            <FormControl size="small" sx={{ ml: 1, minWidth: 150 }}>
              <Select
                value={editedSampleType}
                onChange={(e) => setEditedSampleType(e.target.value)}
                disabled={isSaving}
              >
                <MenuItem value="isolate">isolate</MenuItem>
                <MenuItem value="metagenomic">metagenomic</MenuItem>
              </Select>
            </FormControl>
          </Box>
        ) : (
          <p>
            <b>Sample Type:</b>
            {' '}
            {sample?.attributes.sample_type || 'Unknown'}
            {sample?.attributes.sample_type === 'isolate' && positiveIdentifiedSpeciesCt > 1 && (
              <Tooltip title="Potentially contaminated" arrow>
                <ErrorIcon fontSize="small" color="error" sx={{ ml: 0.5, verticalAlign: 'middle' }} />
              </Tooltip>
            )}
          </p>
        )}
        <div>
            <Divider />
            &nbsp;
        </div>
        <p><b>Analysis Version</b>: {analysis?.attributes.metadata.minion_pipeline_version ? (<Link onClick={() => setVersionDialogOpen(true)}sx={{'&:hover': {color: 'orange', cursor: 'pointer', textDecoration: 'underline'}}}>{removePrefixFromAnalysisVersion(analysis.attributes.metadata.minion_pipeline_version)}</Link>) : '-'}</p>
        <p><b>Analysis Started At</b>: {analysis ? new Date(analysis.attributes.created_at).toLocaleString() : null}</p>
        <p><b>Analysis Completed At</b>: {analysis ? new Date(analysis.attributes.updated_at).toLocaleString() : null} <span style={{color: 'gray'}}>{analysisExecutionTimeString}</span></p>
      </Box>
      <IdentifiedSpeciesVersionDialog open={versionDialogOpen} setOpen={setVersionDialogOpen} analysis={analysis} />
    </>
  );
}

export default DetailedSampleMetadataSummary;
